// Aesthetic: Utilitarian AP work queue - slate palette, tight spacing, tabular
// numbers, one emerald accent reserved for the approve action. Built to be
// scanned and dismissed quickly, not admired.
import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Check,
  X,
  FileText,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Pencil,
  Search,
  Loader2,
  ScanLine,
  ArrowRight,
  Paperclip
} from 'lucide-react';
import axios from 'axios';
import { toIsoDate } from '../lib/naming';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { buildArchiveFileName } from '../lib/naming';
import ReviewPreview from './ReviewPreview';

/**
 * Confidence presentation.
 *
 * Colour is never the only signal - every band carries a word and an explicit
 * sentence about what was and was not corroborated, so the meaning survives
 * greyscale, colour blindness, and a reviewer who has never been told what
 * amber is supposed to imply.
 */
const BAND_STYLES = {
  high: {
    label: 'Corroborated',
    chip: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800',
    rail: 'bg-emerald-500 dark:bg-emerald-600',
    blurb: 'Invoice number and date on the page both match the voucher in TIMS.'
  },
  medium: {
    label: 'Partly corroborated',
    chip: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800',
    rail: 'bg-amber-500 dark:bg-amber-600',
    blurb: 'Only one of invoice number or date matched. Check the other before approving.'
  },
  low: {
    label: 'Voucher only',
    chip: 'bg-orange-100 text-orange-900 border-orange-300 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800',
    rail: 'bg-orange-500 dark:bg-orange-600',
    blurb: 'The voucher exists in TIMS, but nothing on the page confirms it is this invoice.'
  },
  none: {
    label: 'Not identified',
    chip: 'bg-slate-200 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
    rail: 'bg-slate-400 dark:bg-slate-600',
    blurb: 'No voucher number could be resolved. Enter the details manually.'
  },
  // Not a confidence band: nothing was read, because a human asked to key it in.
  // Saying "not identified" would imply a failed attempt that never happened.
  manual: {
    label: 'Manual entry',
    chip: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800',
    rail: 'bg-blue-500 dark:bg-blue-600',
    blurb: 'Not read by AI. Type the voucher and use Look up to fill the rest from TIMS.'
  }
};

const FIELD_LABELS = {
  voucherNumber: 'Voucher',
  vendor: 'Vendor Code',
  invoiceNumber: 'Invoice Number',
  invoiceDate: 'Invoice Date'
};

export function ReviewCard({
  document: doc,
  proposal,
  segmentIndex,
  previewUrl,
  isSelected,
  onToggleSelect,
  onApprove,
  onReject,
  onSendToArchive,
  onPreview,
  transform,
  onTransformChange,
  busy
}) {
  const [fields, setFields] = useState(() => ({
    voucherNumber: proposal?.fields?.voucherNumber || '',
    vendor: proposal?.fields?.vendor || '',
    invoiceNumber: proposal?.fields?.invoiceNumber || '',
    invoiceDate: proposal?.fields?.invoiceDate || '',
    fixedAsset: Boolean(proposal?.fields?.fixedAsset)
  }));
  const [expanded, setExpanded] = useState(false);
  const [looking, setLooking] = useState(false);
  const [lookupNote, setLookupNote] = useState(null);

  /**
   * Re-runs the TIMS lookup for whatever voucher the reviewer typed.
   *
   * This is the escape hatch for the commonest failure: the reader misread a
   * handwritten digit. Correcting the voucher refills vendor, invoice number,
   * date and the fixed-asset flag from TIMS, so a bad read costs one edit.
   */
  const lookupVoucher = async () => {
    const voucher = String(fields.voucherNumber || '').trim();
    if (!voucher) return;

    setLooking(true);
    setLookupNote(null);
    try {
      const base = process.env.REACT_APP_API_BASE;
      const route = process.env.REACT_APP_VOUCHER || '/api/grabvoucher';
      const { data } = await axios.get(
        `${base}${route}?voucherNumber=${encodeURIComponent(voucher)}`
      );

      if (data?.success && data.voucher) {
        const v = data.voucher;
        setFields((prev) => ({
          ...prev,
          voucherNumber: voucher,
          vendor: v.VendorNumber || v.VendorId || '',
          invoiceNumber: v.InvoiceNumber || '',
          invoiceDate: toIsoDate(v.InvoiceDate),
          fixedAsset: Boolean(v.FixedAsset)
        }));
        setLookupNote({ ok: true, text: `Filled from TIMS: ${v.VendorNumber} ${v.InvoiceNumber}` });
      } else {
        setLookupNote({ ok: false, text: 'No such voucher in TIMS.' });
      }
    } catch (err) {
      setLookupNote({ ok: false, text: 'Lookup failed. Enter the details by hand.' });
    } finally {
      setLooking(false);
    }
  };

  const manualEntry = !proposal && doc.source === 'manual';
  const band = proposal?.band || 'none';
  const style = manualEntry ? BAND_STYLES.manual : BAND_STYLES[band] || BAND_STYLES.none;

  // Anything the reviewer edits is a correction, and corrections are the whole
  // measurement. Track them so the badge is honest about what changed.
  const edited = useMemo(() => {
    const original = proposal?.fields || {};
    return Object.keys(FIELD_LABELS).filter(
      (key) => String(fields[key] ?? '') !== String(original[key] ?? '')
    );
  }, [fields, proposal]);

  const previewName = useMemo(() => buildArchiveFileName(fields), [fields]);

  const complete = ['voucherNumber', 'vendor', 'invoiceNumber', 'invoiceDate'].every((key) =>
    String(fields[key] || '').trim()
  );

  const update = (key, value) => setFields((prev) => ({ ...prev, [key]: value }));

  // How the page should be archived. Owned by the queue rather than this card,
  // because bulk approve has to send the same rotation and crop the reviewer
  // set here - otherwise approving one at a time and approving fifty at once
  // would file different documents.
  const rotation = transform?.rotation || 0;
  const crops = transform?.crops || {};

  // Every change goes through one atomic update. Rotating turns the crops with
  // the page, so rotation and crops change together; writing them as two calls
  // let the second land with the rotation it had closed over and silently undo
  // the first, which is why rotating used to reset the crop.
  // Crops are keyed by page number in the source file, which is what the
  // backend uses to match a crop to the right sheet after extraction.
  const updateTransform = (fn) =>
    onTransformChange?.((prev) =>
      fn({ rotation: prev?.rotation || 0, crops: prev?.crops || {} })
    );

  const pageRange =
    proposal?.startPage && proposal?.endPage
      ? proposal.startPage === proposal.endPage
        ? `page ${proposal.startPage}`
        : `pages ${proposal.startPage}–${proposal.endPage}`
      : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className={cn(
        'relative flex gap-0 overflow-hidden rounded-lg border bg-white transition-colors dark:bg-slate-900',
        isSelected
          ? 'border-slate-900 dark:border-slate-300'
          : 'border-slate-200 dark:border-slate-800'
      )}
    >
      {/* Confidence rail - redundant with the chip, for fast vertical scanning */}
      <div className={cn('w-1 shrink-0', style.rail)} aria-hidden="true" />

      <div className="min-w-0 flex-1 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              disabled={!complete}
              aria-label={`Select ${doc.fileName} for bulk approval`}
              className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 accent-slate-900 dark:border-slate-600 dark:accent-slate-100"
            />
            <div className="min-w-0">
              {/* What it is now, and what it becomes. The whole job of this card
                  is that transformation, so it reads across the top rather than
                  making the reviewer look to the bottom for the answer. */}
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <button
                  type="button"
                  onClick={onPreview}
                  title={doc.fileName}
                  className="group flex min-w-0 items-center gap-2 text-left"
                >
                  <FileText className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500" />
                  <span className="truncate text-sm font-medium text-slate-500 underline-offset-2 group-hover:underline dark:text-slate-400">
                    {doc.fileName}
                  </span>
                </button>

                <ArrowRight
                  className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-600"
                  aria-hidden="true"
                />

                {complete ? (
                  <span
                    title={previewName}
                    className="min-w-0 truncate font-mono text-sm font-semibold text-slate-900 dark:text-slate-100"
                  >
                    {previewName}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    Fill in all four fields to file
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <Badge variant="outline" className={cn('font-medium', style.chip)}>
                  {style.label}
                </Badge>
                {pageRange && <span className="tabular-nums">{pageRange}</span>}
                {proposal?.source === 'vision' && (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800">
                    read by model
                  </span>
                )}
                {edited.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                    <Pencil className="h-3 w-3" />
                    {edited.length} edit{edited.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {/* The reader called 16 of 16 real slips correctly, but "obvious"
                is not "always" - and a misread slip would otherwise sit here
                demanding a voucher number it does not have. */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onSendToArchive?.(doc.id, segmentIndex)}
              disabled={busy}
              // The ghost variant hovers to the theme accent, which is the same
              // teal as everything else on this card. These two buttons take the
              // document out of the queue, so they get their own colour on hover
              // rather than reading as one more neutral action.
              className="text-slate-500 transition-colors hover:bg-yellow-400 hover:text-black dark:text-slate-400 dark:hover:bg-yellow-500 dark:hover:text-black"
              title="This is a packing slip, not an invoice. Send it to Search Archive to be attached to its voucher."
            >
              <Paperclip className="mr-1 h-4 w-4" />
              Packing slip
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onReject(doc.id)}
              disabled={busy}
              className="text-slate-500 transition-colors hover:bg-red-600 hover:text-white dark:text-slate-400 dark:hover:bg-red-600 dark:hover:text-white"
            >
              <X className="mr-1 h-4 w-4" />
              Reject
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => onApprove(doc.id, segmentIndex, fields, transform)}
              disabled={busy || !complete}
              className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 dark:bg-emerald-700 dark:hover:bg-emerald-600"
            >
              <Check className="mr-1 h-4 w-4" />
              Approve
            </Button>
          </div>
        </div>

        <p className="mt-3 text-xs text-slate-600 dark:text-slate-400">{style.blurb}</p>

        {/* The scan sits beside the fields, not behind a link. Approving means
            checking the machine against the paper, so the paper has to be here. */}
        <div className="mt-3 grid gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
          <ReviewPreview
            url={previewUrl}
            startPage={proposal?.startPage}
            endPage={proposal?.endPage}
            fileName={doc.fileName}
            onExpand={onPreview}
            rotation={rotation}
            crops={crops}
            onTransformChange={updateTransform}
          />

          <div className="flex flex-col gap-3">
            {/* Voucher gets its own row with a lookup, because it is the field
                most likely to be wrong: handwriting is hard, and a scan often
                carries several numbers. Correcting it refills everything else
                from TIMS, so a bad read costs one edit rather than four. */}
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-3">
                <label
                  htmlFor={`${doc.id}-${segmentIndex}-voucherNumber`}
                  className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
                >
                  Voucher
                </label>

                {/* Fixed asset lives with the fields it changes: it appends _FA
                    to the filename shown at the top of the card. */}
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                  <input
                    type="checkbox"
                    checked={Boolean(fields.fixedAsset)}
                    onChange={(e) => update('fixedAsset', e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-slate-300 accent-slate-900 dark:border-slate-600 dark:accent-slate-100"
                  />
                  Fixed asset
                </label>
              </div>
              <div className="flex gap-2">
                <Input
                  id={`${doc.id}-${segmentIndex}-voucherNumber`}
                  value={fields.voucherNumber || ''}
                  onChange={(e) => update('voucherNumber', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      lookupVoucher();
                    }
                  }}
                  className={cn(
                    'h-8 flex-1 text-sm tabular-nums',
                    edited.includes('voucherNumber') &&
                      'border-blue-400 bg-blue-50 dark:border-blue-600 dark:bg-blue-950/40'
                  )}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={lookupVoucher}
                  disabled={looking || !String(fields.voucherNumber || '').trim()}
                >
                  {looking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                  <span className="ml-1">Look up</span>
                </Button>
              </div>
              {lookupNote && (
                <p
                  className={cn(
                    'text-[11px]',
                    lookupNote.ok
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400'
                  )}
                >
                  {lookupNote.text}
                </p>
              )}
            </div>

            {/* The remaining fields, pre-filled. Editing one is the correction signal. */}
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(FIELD_LABELS)
                .filter(([key]) => key !== 'voucherNumber')
                .map(([key, label]) => (
                  <div key={key} className="space-y-1">
                    <label
                      htmlFor={`${doc.id}-${segmentIndex}-${key}`}
                      className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
                    >
                      {label}
                    </label>
                    <Input
                      id={`${doc.id}-${segmentIndex}-${key}`}
                      type={key === 'invoiceDate' ? 'date' : 'text'}
                      value={fields[key] || ''}
                      onChange={(e) => update(key, e.target.value)}
                      className={cn(
                        'h-8 text-sm tabular-nums',
                        edited.includes(key) &&
                          'border-blue-400 bg-blue-50 dark:border-blue-600 dark:bg-blue-950/40'
                      )}
                    />
                  </div>
                ))}
            </div>

            {/* What the reader actually transcribed, verbatim, beside the scan it
                came from. Lets the reviewer judge the read itself, not just the
                conclusion drawn from it. */}
            {proposal?.readout && (
              <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950">
                <div className="flex items-center gap-1.5">
                  <ScanLine className="h-3 w-3 text-slate-400 dark:text-slate-500" />
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Read from this page
                  </span>
                </div>
                <pre className="mt-1.5 max-h-32 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-slate-700 dark:text-slate-300">
                  {proposal.readout}
                </pre>
              </div>
            )}

            {/* Which numbers were tried and what each turned out to be. When the
                read is wrong this is usually where the reviewer spots it. */}
            {proposal?.candidatesTried?.length > 0 && (
              <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-950">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Numbers tried
                </span>
                <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  {proposal.candidatesTried.map((c, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-1 font-mono text-[11px] text-slate-600 dark:text-slate-400"
                    >
                      {c.band === 'high' ? (
                        <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <X className="h-3 w-3 text-slate-400 dark:text-slate-600" />
                      )}
                      {c.candidate}
                      {!c.found && <span className="text-slate-400 dark:text-slate-600">(unknown)</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {proposal?.evidence?.length > 0 && (
              <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-900 dark:bg-emerald-950/40">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                  Matched against TIMS
                </span>
                <ul className="mt-1 space-y-0.5">
                  {proposal.evidence.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300"
                    >
                      <Check className="h-3 w-3 shrink-0 text-emerald-600 dark:text-emerald-400" />
                      <span className="font-mono">{item.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            Why this answer
          </button>
        </div>

        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.2 }}
            className="mt-3 space-y-2 overflow-hidden border-t border-slate-200 pt-3 text-xs dark:border-slate-800"
          >
            {(proposal?.evidence || []).length > 0 ? (
              <ul className="space-y-1">
                {proposal.evidence.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-600 dark:text-slate-400">
                    <Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    <span>
                      <span className="font-medium text-slate-900 dark:text-slate-200">
                        {item.value}
                      </span>{' '}
                      &mdash; {item.note}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="flex items-start gap-2 text-slate-600 dark:text-slate-400">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                Nothing on the page corroborated the voucher.
              </p>
            )}

            {proposal?.visionNote && (
              <p className="text-slate-500 dark:text-slate-400">
                <span className="font-medium">Model note:</span> {proposal.visionNote}
              </p>
            )}
            {proposal?.segmentReasoning && (
              <p className="text-slate-500 dark:text-slate-400">
                <span className="font-medium">Page split:</span> {proposal.segmentReasoning}
              </p>
            )}
            {(proposal?.candidatesTried || []).length > 0 && (
              <p className="text-slate-500 dark:text-slate-400">
                <span className="font-medium">Candidates tried:</span>{' '}
                {proposal.candidatesTried
                  .map((c) => `${c.candidate}${c.found ? '' : ' (not in TIMS)'}`)
                  .join(', ')}
              </p>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default ReviewCard;
